import { useState } from "react";
import { List, useTable } from "@refinedev/antd";
import { Table, Space, Button, Tag, Typography } from "antd";
import { GlassCard } from "../../shared/components";
import { EditOutlined, EyeOutlined, LinkOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslate } from "@refinedev/core";
import { useGo } from "@refinedev/core";
import { PricingPlanCreateModal } from "./PricingPlanCreateModal";
import { PricingPlanEditModal } from "./PricingPlanEditModal";
import { PricingPlanAccountsModal } from "./PricingPlanAccountsModal";

export const PricingPlanList = ({ hideHeader }: { hideHeader?: boolean } = {}) => {
  const { tableProps, tableQuery } = useTable({
    resource: "pricing-plans",
    syncWithLocation: true,
  });

  const _t = useTranslate();
  const go = useGo();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<number | string | null>(null);
  const [accountsModalOpen, setAccountsModalOpen] = useState(false);
  const [accountsPlanId, setAccountsPlanId] = useState<number | string | null>(null);

  const openAccountsModal = (planId: number | string) => {
    setAccountsPlanId(planId);
    setAccountsModalOpen(true);
  };

  const { Text } = Typography;

  return (
    <div className="max-w-7xl mx-auto">
      <List
        title={hideHeader ? false : undefined}
        breadcrumb={hideHeader ? false : undefined}
        headerButtons={() => [
          <Button
            key="create"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            {_t("pricing-plans.titles.create")}
          </Button>,
        ]}
      >
        <style>{`
                .pricing-plans-list-table-view .ant-table { background: transparent !important; }
                .pricing-plans-list-table-view .ant-table-thead > tr > th { background: transparent !important; }
                .pricing-plans-list-table-view .ant-table-tbody > tr > td { background: transparent !important; }
            `}</style>
        <GlassCard className="pricing-plans-list-table-view !p-0 overflow-hidden">
          <Table {...tableProps} rowKey="id">
            <Table.Column
              dataIndex="name"
              title={_t("pricing-plans.fields.name")}
              render={(value: string, record: any) => {
                return (
                  <Space direction="vertical" size={0}>
                    <Text strong>{value}</Text>
                    <Text type="secondary" className="!text-xs">
                      #{record.id}
                    </Text>
                  </Space>
                );
              }}
            />
            <Table.Column
              dataIndex="description"
              title={_t("pricing-plans.fields.description")}
            />
            <Table.Column
              dataIndex="productCount"
              title={_t("pricing-plans.fields.products")}
              render={(value: number) => <Tag color="orange">{value}</Tag>}
            />
            <Table.Column
              dataIndex="accountCount"
              title={_t("pricing-plans.fields.accounts")}
              render={(value: number, record: { id: number | string }) => (
                <Tag
                  color="orange"
                  className="cursor-pointer"
                  onClick={() => openAccountsModal(record.id)}
                >
                  {value}
                </Tag>
              )}
            />
            <Table.Column
              dataIndex="isActive"
              title={_t("pricing-plans.fields.isActive")}
              render={(value) => (
                <Tag color={value ? "green" : "red"}>
                  {value ? _t("titles.yes") : _t("titles.no")}
                </Tag>
              )}
            />
            <Table.Column
              title={_t("table.actions")}
              dataIndex="actions"
              render={(_, record) => (
                <Space>
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => {
                      go({ to: `/pricing-plans/show/${record.id}` });
                    }}
                  />
                  <Button
                    icon={<LinkOutlined />}
                    title={_t("pricing-plans.actions.manageAccounts")}
                    onClick={() => openAccountsModal(record.id as number | string)}
                  />
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditingPlanId(record.id as number | string);
                      setEditModalOpen(true);
                    }}
                  />
                </Space>
              )}
            />
          </Table>
        </GlassCard>
        <PricingPlanCreateModal
          open={createModalOpen}
          onCancel={() => setCreateModalOpen(false)}
          onSuccess={() => {
            setCreateModalOpen(false);
            tableQuery?.refetch();
          }}
        />
        <PricingPlanEditModal
          open={editModalOpen}
          planId={editingPlanId}
          onCancel={() => {
            setEditModalOpen(false);
            setEditingPlanId(null);
          }}
          onSuccess={() => {
            setEditModalOpen(false);
            setEditingPlanId(null);
            tableQuery?.refetch();
          }}
        />
        <PricingPlanAccountsModal
          open={accountsModalOpen}
          planId={accountsPlanId}
          onCancel={() => {
            setAccountsModalOpen(false);
            setAccountsPlanId(null);
          }}
          onSuccess={() => tableQuery?.refetch()}
        />
      </List>
    </div>
  );
};
